import { OmitStrict } from "@supernova-studio/model";
import PQueue from "p-queue";
import * as Y from "yjs";
import {
  DTODocumentationHierarchyV2,
  DTODocumentationSettings,
  DTOElementActionInput,
  DTOElementActionInputOfType,
  DTOPageBlockDefinition,
} from "../api";
import { DocumentationPageEditorModel, generatePageContentHash, VersionRoomBaseYDoc } from "../yjs";
import { computeDocsHierarchy } from "../yjs/version-room/compute-dto";
import { VersionRoomBaseYDocState } from "../yjs/version-room/types";
import { applyActionsLocally } from "./local-action-executor";
import { SupportedActionType } from "./types";

type YObserver = () => void;
type HierarchyObserver = (hierarchy: DTODocumentationHierarchyV2) => void;
type SettingsObserver = (settings: DTODocumentationSettings) => void;
type ErrorObserver<T> = (error: unknown, actionMetadata: T | undefined) => void;

type TransactionExecutor = (trx: DTOElementActionInput) => Promise<void>;
type InternalTransactionExecutor<T> = (trx: InternalAction<T>) => Promise<void>;
type TransactionIdGenerator = () => string;

type Action = DTOElementActionInputOfType<SupportedActionType>;
type InputAction<T> = OmitStrict<Action, "tId">;
type InternalAction<T> = { action: Action; metadata?: T };

export class DocsStructureRepository<T> {
  private userId: string;
  private designSystemVersionId: string;

  private readonly yDoc: Y.Doc;
  private readonly yObserver: YObserver;
  private _yState: VersionRoomBaseYDocState | undefined;

  private _currentHierarchy: DTODocumentationHierarchyV2 | undefined;
  private _currentSettings: DTODocumentationSettings | undefined;

  private localActions: DTOElementActionInput[] = [];
  private readonly actionQueue: TransactionQueue<T>;
  private readonly hierarchyObservers = new Set<HierarchyObserver>();
  private readonly settingsObservers = new Set<SettingsObserver>();
  private readonly errorObservers = new Set<ErrorObserver<T>>();
  private readonly initCallbacks = new Set<() => void>();

  private readonly transactionIdGenerator: TransactionIdGenerator;
  private readonly transactionExecutor: TransactionExecutor;

  constructor(config: {
    userId: string;
    designSystemVersionId: string;
    yDoc: Y.Doc;
    transactionExecutor: TransactionExecutor;
    transactionIdGenerator: TransactionIdGenerator;
  }) {
    this.userId = config.userId;
    this.designSystemVersionId = config.designSystemVersionId;
    this.yDoc = config.yDoc;

    this.yObserver = this.yDoc.on("update", () => this.onYUpdate()) as YObserver;
    this.onYUpdate();

    this.transactionExecutor = config.transactionExecutor;
    this.transactionIdGenerator = config.transactionIdGenerator;

    this.actionQueue = new TransactionQueue(action => this.executeInternalAction(action));
  }

  //
  // Lifecycle
  //

  get isInitialized(): boolean {
    return !!this._currentHierarchy;
  }

  onInitialized(): Promise<void> {
    if (this.isInitialized) return Promise.resolve();
    return new Promise(resolve => {
      this.initCallbacks.add(resolve);
    });
  }

  addHierarchyObserver(observer: HierarchyObserver) {
    this.hierarchyObservers.add(observer);
    if (this._currentHierarchy) observer(this._currentHierarchy);
  }

  removeHierarchyObserver(observer: HierarchyObserver) {
    this.hierarchyObservers.delete(observer);
  }

  addSettingsObserver(observer: SettingsObserver) {
    this.settingsObservers.add(observer);
    if (this._currentSettings) observer(this._currentSettings);
  }

  removeSettingsObserver(observer: SettingsObserver) {
    this.settingsObservers.delete(observer);
  }

  addErrorObserver(observer: ErrorObserver<T>) {
    this.errorObservers.add(observer);
  }

  removeErrorObserver(observer: ErrorObserver<T>) {
    this.errorObservers.delete(observer);
  }

  dispose() {
    this.yDoc.off("update", this.yObserver);
    this.hierarchyObservers.clear();
    this.settingsObservers.clear();
    this.errorObservers.clear();
    this.actionQueue.clear();
  }

  //
  // Accessors
  //

  get currentHierarchy(): DTODocumentationHierarchyV2 {
    const hierarchy = this._currentHierarchy;
    if (!hierarchy) throw new Error(`Hierarchy cannot be accessed while it's still loading`);
    return hierarchy;
  }

  //
  // Actions
  //

  executeAction(action: InputAction<T>, metadata?: T) {
    void this.executeActionPromise(action, metadata);
  }

  executeActionPromise(action: InputAction<T>, metadata?: T) {
    const fullAction: DTOElementActionInput = { ...action, tId: this.transactionIdGenerator() };
    this.localActions.push(fullAction);
    this.refreshHierarchy();
    return this.actionQueue.enqueue({ action: fullAction, metadata });
  }

  notifyPageContentUpdated(
    pagePersistentId: string,
    content: DocumentationPageEditorModel,
    definitions: DTOPageBlockDefinition[]
  ) {
    const pageContentHash = generatePageContentHash(content, definitions);
    new VersionRoomBaseYDoc(this.yDoc).updateDocumentationPageContentHashes({
      [pagePersistentId]: pageContentHash,
    });
  }

  private async executeInternalAction(action: InternalAction<T>) {
    try {
      return await this.transactionExecutor(action.action);
    } catch (e: unknown) {
      // Remove this action from local actions and reload hierarchy
      this.localActions = this.localActions.filter(a => a.tId !== action.action.tId);
      this.refreshHierarchy();

      this.errorObservers.forEach(o => o(e, action.metadata));
    }
  }

  //
  // Reactions
  //

  private refreshState() {
    this.refreshHierarchy();
    this.refreshSettings();
  }

  private refreshSettings() {
    const yState = this._yState;
    if (!yState) return;

    // Calculate new settings
    const newSettings: DTODocumentationSettings = {
      isApprovalRequiredForPublishing: yState.settings.approvalRequiredForPublishing,
      isApprovalsFeatureEnabled: yState.settings.isApprovalFeatureEnabled,
      isDraftFeatureAdopted: yState.settings.isDraftFeatureAdopted,
    };

    // Only invoke when changed
    if (
      !this._currentSettings ||
      newSettings.isApprovalRequiredForPublishing !== this._currentSettings.isApprovalRequiredForPublishing ||
      newSettings.isApprovalsFeatureEnabled !== this._currentSettings.isApprovalsFeatureEnabled ||
      newSettings.isDraftFeatureAdopted !== this._currentSettings.isDraftFeatureAdopted
    ) {
      this._currentSettings = newSettings;

      // Invoke observers
      this.settingsObservers.forEach(o => o(newSettings));
    }
  }

  private refreshHierarchy() {
    const yState = this._yState;
    if (!yState) return;

    // Calculate new hierarchy
    const hierarchy = this.calculateHierarchy(yState);
    if (!hierarchy) return;
    this._currentHierarchy = hierarchy;

    // Invoke observers
    this.hierarchyObservers.forEach(o => o(hierarchy));
  }

  private calculateHierarchy(yState: VersionRoomBaseYDocState) {
    // Remove local actions that have been applied remotely
    const executedTransactionIds = new Set(yState.executedTransactionIds);
    const localActions = this.localActions.filter(a => a.tId && !executedTransactionIds.has(a.tId));
    this.localActions = localActions;

    // Apply local actions onto the Yjs state
    const { pages, groups, approvals, pageLiveblockRoomIds } = applyActionsLocally({
      userId: this.userId,
      designSystemVersionId: this.designSystemVersionId,
      remoteState: yState,
      actions: localActions,
    });

    const hierarchy = computeDocsHierarchy(
      {
        // Merged remote + local
        pages,
        groups,
        approvals,
        pageLiveblockRoomIds,

        // Remote only
        groupSnapshots: yState.groupSnapshots,
        pageContentHashes: yState.pageContentHashes,
        pageSnapshots: yState.pageSnapshots,
        settings: yState.settings,
      },
      { includeDeletedContent: true }
    );

    return hierarchy;
  }

  private onYUpdate() {
    const newState = new VersionRoomBaseYDoc(this.yDoc).getState();
    if (newState.groups.length) {
      this._yState = newState;
      this.refreshState();

      // Flush init callbacks
      this.initCallbacks.forEach(f => f());
      this.initCallbacks.clear();
    }
  }
}

class TransactionQueue<T> {
  private readonly executor: InternalTransactionExecutor<T>;
  private readonly queue = new PQueue({
    concurrency: 1,
  });

  constructor(executor: InternalTransactionExecutor<T>) {
    this.executor = executor;
  }

  enqueue(trx: InternalAction<T>) {
    return this.queue.add(() => this.executor(trx));
  }

  onEmpty() {
    return this.queue.onEmpty();
  }

  clear() {
    this.queue.clear();
  }
}
